/**
 * DM Audit Dashboard — filterable, paginated view of every mutation in the
 * session. Admin-only route; players see the simpler Activity panel on the
 * Party page and the Session Log on the Sheet page.
 *
 * All filtering is client-side: the API returns newest-first pages and the
 * dashboard holds a growing buffer. Good enough for a small private campaign
 * where total audit rows number in the hundreds, not millions.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AuditEntry,
  AuditPage,
  DEFAULT_SESSION_ID,
  getAuditLog,
  getCharacters,
} from '../lib/sessionApi';

/** Action categories for the filter dropdown. */
const ACTION_GROUPS: Record<string, string[]> = {
  'HP':          ['character/hp'],
  'Spell Slots': ['character/slots'],
  'Hit Dice':    ['character/hit-dice'],
  'Inspiration': ['character/inspiration'],
  'Conditions':  ['character/conditions'],
  'Resources':   ['character/resources'],
  'Inventory':   ['character/inventory'],
  'Dice Roll':   ['session/roll'],
  'Party':       ['party/update'],
  'Loot':        ['loot/add', 'loot/update', 'loot/remove'],
  'Session':     ['session/start', 'session/end'],
};

export default function AuditDashboard() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  // Character roster for the filter dropdown.
  const [characters, setCharacters] = useState<Array<{ id: string; name: string }>>([]);

  // Filters (all client-side).
  const [filterPlayer, setFilterPlayer] = useState<string>('');
  const [filterAction, setFilterAction] = useState<string>('');
  const [filterCharacter, setFilterCharacter] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState<string>('');

  const PAGE_SIZE = 100;

  const loadPage = useCallback(
    async (before?: string) => {
      setLoading(true);
      setError(null);
      try {
        const page: AuditPage = await getAuditLog(DEFAULT_SESSION_ID, {
          limit: PAGE_SIZE,
          before: before ?? undefined,
        });
        setEntries((prev) => (before ? [...prev, ...page.entries] : page.entries));
        setCursor(page.nextBefore);
        setHasMore(page.nextBefore != null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load audit log');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadPage();
    getCharacters(DEFAULT_SESSION_ID)
      .then((list) =>
        setCharacters(
          list.characters.map((b) => ({
            id: b.id,
            name: (b.profile as { name?: string } | null)?.name ?? b.id,
          })),
        ),
      )
      .catch(() => {});
  }, [loadPage]);

  // Derive the unique player names from the loaded entries (avoids a
  // separate Cognito list-users call).
  const playerNames = useMemo(() => {
    const set = new Set<string>();
    for (const e of entries) {
      if (e.actorUsername) set.add(e.actorUsername);
    }
    return Array.from(set).sort();
  }, [entries]);

  // Client-side filtering.
  const filtered = useMemo(() => {
    const searchLc = filterSearch.toLowerCase();
    return entries.filter((e) => {
      if (filterPlayer && e.actorUsername !== filterPlayer) return false;
      if (filterAction) {
        const group = ACTION_GROUPS[filterAction];
        if (group && !group.includes(e.action)) return false;
      }
      if (filterCharacter && e.characterId !== filterCharacter) return false;
      if (searchLc) {
        const haystack = `${e.summary} ${e.reason ?? ''} ${e.action}`.toLowerCase();
        if (!haystack.includes(searchLc)) return false;
      }
      return true;
    });
  }, [entries, filterPlayer, filterAction, filterCharacter, filterSearch]);

  const clearFilters = () => {
    setFilterPlayer('');
    setFilterAction('');
    setFilterCharacter('');
    setFilterSearch('');
  };
  const hasFilters = !!(filterPlayer || filterAction || filterCharacter || filterSearch);

  return (
    <div style={{ maxWidth: '900px' }}>
      <h1>Audit Log</h1>
      <p style={{ color: 'var(--fg-muted)', marginBottom: '1rem' }}>
        {entries.length} entries loaded · {filtered.length} shown
        {hasFilters && (
          <button onClick={clearFilters} style={{ marginLeft: '0.75rem' }}>
            Clear filters
          </button>
        )}
      </p>

      {error && (
        <p style={{ color: 'var(--danger, #e06c75)', marginBottom: '0.75rem' }}>
          {error}
        </p>
      )}

      {/* Filter bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '1rem',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>Player</span>
          <select value={filterPlayer} onChange={(e) => setFilterPlayer(e.target.value)}>
            <option value="">All</option>
            {playerNames.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>Action</span>
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
            <option value="">All</option>
            {Object.keys(ACTION_GROUPS).map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>Character</span>
          <select value={filterCharacter} onChange={(e) => setFilterCharacter(e.target.value)}>
            <option value="">All</option>
            {characters.map((ch) => (
              <option key={ch.id} value={ch.id}>{ch.name}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.85rem', flex: '1 1 12rem' }}>
          <span style={{ color: 'var(--fg-muted)' }}>Search</span>
          <input
            type="text"
            placeholder="summary, reason, action…"
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
          />
        </label>
      </div>

      {/* Table */}
      {filtered.length === 0 && !loading ? (
        <p style={{ color: 'var(--fg-muted)' }}>
          {entries.length === 0 ? 'No audit entries yet.' : 'No entries match the current filters.'}
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '0.4rem 0.5rem' }}>Time</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>Player</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>Action</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>Character</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>Summary</th>
              <th style={{ padding: '0.4rem 0.5rem' }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const charName = e.characterId
                ? characters.find((c) => c.id === e.characterId)?.name ?? e.characterId
                : '';
              // Friendly action label — strip the namespace prefix.
              const actionLabel = e.action.includes('/')
                ? e.action.split('/').slice(1).join('/')
                : e.action;
              return (
                <tr
                  key={e.id}
                  style={{ borderBottom: '1px solid var(--border)' }}
                >
                  <td
                    className="numeric"
                    style={{
                      padding: '0.35rem 0.5rem',
                      color: 'var(--fg-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {new Date(e.ts).toLocaleString()}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-muted)' }}>
                    {e.actorUsername}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '0.1rem 0.45rem',
                        borderRadius: '999px',
                        background: 'var(--bg-elevated, #2a2d34)',
                        border: '1px solid var(--border)',
                        fontSize: '0.8rem',
                      }}
                    >
                      {actionLabel}
                    </span>
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>{charName}</td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>{e.summary}</td>
                  <td style={{ padding: '0.35rem 0.5rem', color: 'var(--fg-muted)' }}>
                    {e.reason ?? ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Paging */}
      {hasMore && (
        <button
          onClick={() => cursor && loadPage(cursor)}
          disabled={loading}
          style={{ marginTop: '0.75rem' }}
        >
          {loading ? 'Loading…' : `Load more (${PAGE_SIZE})`}
        </button>
      )}
    </div>
  );
}
